'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowDownRightIcon from '@flipster/react/icon/generated/ArrowDownRightIcon';
import ArrowUpRightIcon from '@flipster/react/icon/generated/ArrowUpRightIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';

import { SeparatorTitle } from '../../component/separator-title';
import { resultAnnouncementCardBunja } from './result-announcement-card.state';

export type ResultStatus = 'correct' | 'incorrect' | 'not_participated';

export const ResultAnnouncementCard = () => {
  const {
    roundAtom,
    resultStatusAtom,
    myPredictionAtom,
    btcPriceChangeInfoAtom,
    rewardAmountAtom,
    upRateAtom,
    downRateAtom,
  } = useBunja(resultAnnouncementCardBunja);

  const round = useAtomValue(roundAtom);
  const resultStatus = useAtomValue(resultStatusAtom);
  const myPrediction = useAtomValue(myPredictionAtom);
  const { btcPriceChange, btcPriceDirection } = useAtomValue(btcPriceChangeInfoAtom);
  const rewardAmount = useAtomValue(rewardAmountAtom);
  const upRate = useAtomValue(upRateAtom);
  const downRate = useAtomValue(downRateAtom);

  const isUp = btcPriceDirection === 'UP';
  const didParticipate = resultStatus !== 'not_participated';

  return (
    <div className={vstack({ gap: 24, w: '100%', alignItems: 'stretch' })}>
      <SeparatorTitle
        colorSet='keyColor'
        title={<FormattedMessage id='Campaign.r260123.Day_number_results_announced' values={{ number: round }} />}
      />

      <div
        className={css({
          w: '100%',
          p: 40,
          rounded: 16,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-2',
          bg: 'background.default.level-0',
          display: 'flex',
          flexDirection: { base: 'column', tablet: 'row' },
          gap: { base: 32, tablet: 80 },
          alignItems: { base: 'stretch', tablet: 'center' },
        })}
      >
        <ResultLeftSection round={round} resultStatus={resultStatus} rewardAmount={rewardAmount} />

        <div className={vstack({ flex: 1, alignItems: 'stretch' })}>
          {didParticipate && myPrediction && (
            <div className={hstack({ gap: 80, pb: 24, alignItems: 'flex-start' })}>
              <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3', w: 150 })}>
                <FormattedMessage id='Campaign.r260123.Your_prediction' />
              </span>
              <div className={hstack({ flex: 1, gap: 4, alignItems: 'center', justifyContent: 'flex-end' })}>
                {myPrediction === 'UP' ? (
                  <ArrowUpRightIcon size={16} className={css({ color: 'content.state.positive' })} />
                ) : (
                  <ArrowDownRightIcon size={16} className={css({ color: 'content.state.negative' })} />
                )}
                <span
                  className={css({
                    textStyle: 'Monospace/Title/XS',
                    color: myPrediction === 'UP' ? 'content.state.positive' : 'content.state.negative',
                  })}
                >
                  {myPrediction === 'UP' ? (
                    <FormattedMessage id='Campaign.r260123.Going_up_non_capital' />
                  ) : (
                    <FormattedMessage id='Campaign.r260123.Going_down_non_capital' />
                  )}
                </span>
              </div>
            </div>
          )}

          <div
            className={hstack({
              gap: 80,
              pb: 24,
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderColor: 'border.default.level-1',
              alignItems: 'flex-start',
            })}
          >
            <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3', w: 150 })}>
              <FormattedMessage id='Campaign.r260123.Symbol_price_change' values={{ symbol: 'BTC' }} />
            </span>
            <div className={hstack({ flex: 1, gap: 4, alignItems: 'center', justifyContent: 'flex-end' })}>
              {isUp ? (
                <ArrowUpRightIcon size={16} className={css({ color: 'content.state.positive' })} />
              ) : (
                <ArrowDownRightIcon size={16} className={css({ color: 'content.state.negative' })} />
              )}
              <span
                className={css({
                  textStyle: 'Monospace/Title/XS',
                  color: isUp ? 'content.state.positive' : 'content.state.negative',
                })}
              >
                <FormattedMessage id='Campaign.r260123.Amount_up' values={{ amount: btcPriceChange }} />
              </span>
            </div>
          </div>

          <div
            className={css({
              display: 'flex',
              flexDirection: { base: 'column', tablet: 'row' },
              gap: { base: 16, tablet: 80 },
              pt: 24,
              alignItems: { base: 'stretch', tablet: 'center' },
            })}
          >
            <span
              className={css({
                textStyle: 'Text/M/Regular',
                color: 'content.default.level-3',
                w: { base: 'auto', tablet: 150 },
              })}
            >
              <FormattedMessage id='Campaign.r260123.Vote_distribution' />
            </span>
            <div className={hstack({ flex: 1, gap: 8, alignItems: 'center' })}>
              <span className={css({ textStyle: 'Title/XS', color: 'content.state.positive', flexShrink: 0 })}>
                {upRate}%
              </span>
              <div className={hstack({ flex: 1, h: 8, overflow: 'hidden' })}>
                <div
                  className={css({ h: '100%', bg: 'content.state.positive', rounded: 1000 })}
                  style={{ width: `${upRate}%` }}
                />
                <div
                  className={css({ h: '100%', bg: 'content.state.negative', rounded: 1000 })}
                  style={{ width: `${downRate}%` }}
                />
              </div>
              <span className={css({ textStyle: 'Title/XS', color: 'content.state.negative', flexShrink: 0 })}>
                {downRate}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const containerStyle = vstack({ gap: 16, alignItems: 'flex-start', w: { base: '100%', tablet: 400 }, flexShrink: 0 });
const titleStyle = css({ textStyle: 'Title/L', color: 'content.default.level-5' });

interface RewardCardProps {
  label: React.ReactNode;
  amount: string;
  amountStyle?: 'Title/L/Monospace' | 'Display/L';
}

const RewardCard = ({ label, amount, amountStyle = 'Title/L/Monospace' }: RewardCardProps) => (
  <div
    className={vstack({
      gap: 8,
      w: '100%',
      px: 20,
      py: 16,
      rounded: 16,
      bg: 'background.default.level-1',
      alignItems: 'flex-start',
    })}
  >
    <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>{label}</span>
    <span className={css({ color: 'content.state.positive', textStyle: amountStyle })}>
      +
      <Formatted.Number value={amount} toFixed precision={UsdtPrecision} />
    </span>
  </div>
);

interface ResultLeftSectionProps {
  round: number;
  resultStatus: ResultStatus;
  rewardAmount: string;
}

const ResultLeftSection = ({ round, resultStatus, rewardAmount }: ResultLeftSectionProps) => {
  const nextRound = round + 1;

  if (resultStatus === 'correct') {
    return (
      <div className={containerStyle}>
        <span className={titleStyle}>
          <FormattedMessage id='Campaign.r260123.Your_prediction_was_correct' />
        </span>
        <div className={vstack({ gap: 16, w: '100%', alignItems: 'flex-start' })}>
          <span className={css({ textStyle: 'Title/XS', color: 'content.default.level-3' })}>
            <FormattedMessage id='Campaign.r260123.Your_reward_is_ready' />
          </span>
          <RewardCard
            label={<FormattedMessage id='Campaign.r260123.My_round_number_reward_usdt' values={{ number: round }} />}
            amount={rewardAmount}
          />
        </div>
      </div>
    );
  }

  if (resultStatus === 'incorrect') {
    return (
      <div className={containerStyle}>
        <span className={titleStyle}>
          <FormattedMessage id='Campaign.r260123.Your_prediction_was_incorrect' />
        </span>
        <div className={vstack({ gap: 16, w: '100%', alignItems: 'flex-start' })}>
          <span className={css({ textStyle: 'Title/S', color: 'content.default.level-3' })}>
            <FormattedMessage id='Campaign.r260123.Try_again_in_round_number' values={{ number: nextRound }} />
          </span>
          <RewardCard
            label={<FormattedMessage id='Campaign.r260123.Reward_if_you_had_been_correct_usdt' />}
            amount={rewardAmount}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={containerStyle}>
      <div className={vstack({ gap: 8, alignItems: 'flex-start', w: '100%' })}>
        <span className={titleStyle}>
          <FormattedMessage id='Campaign.r260123.The_highest_reward_in_day_number_was' values={{ number: round }} />
        </span>
        <span className={css({ color: 'content.state.positive', textStyle: 'Display/L' })}>
          +
          <Formatted.Number value={rewardAmount} toFixed precision={UsdtPrecision} />
        </span>
      </div>
      <span className={css({ textStyle: 'Title/XS', color: 'content.default.level-3' })}>
        <FormattedMessage
          id='Campaign.r260123.Try_for_a_higher_reward_in_round_number'
          values={{ number: nextRound }}
        />
      </span>
    </div>
  );
};
